import { useMutation, useQueryClient } from "@tanstack/react-query";
import { pathPickBasketMutationApi } from "@/features/pathpick/features/basket/api/mutation";
import { PathPickBasketDeleteRequestBody } from "@/features/pathpick/features/basket/types/request-body";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { refreshPathPickProducts } from "@/features/pathpick/utils/query-cache";

const useDeletePathPickBasket = () => {
  const { companyId } = useCompanyIdentity();
  const queryClient = useQueryClient();
  const { t } = useI18nHelper();
  const { info, error } = useAppToasts({ id: "GLOBAL" });

  return useMutation({
    mutationFn: (body: PathPickBasketDeleteRequestBody) =>
      pathPickBasketMutationApi.deletePathPickBasket(companyId as number, body),
    onSuccess: () => {
      // 담은 상품 목록/헤더 카운트 갱신
      // 담은 상품 수 배지(pathpick/counts)도 함께 갱신
      queryClient.invalidateQueries({
        queryKey: ["pathpick", "counts", companyId],
      });
      // 검색 페이지 상품의 담김(isInBasket) 상태 해제 반영
      // — 화면에 없는 상품 찾기 목록은 비워, 돌아왔을 때 담김 표시가 뒤늦게 풀리지 않게 한다.
      refreshPathPickProducts(queryClient, companyId);
      // 전체 선택 대상(ids_only)과 "전체 n개 선택"의 n도 같은 변화를 따라간다.
      queryClient.invalidateQueries({
        queryKey: ["pathpick", "api-product-ids", companyId],
      });
      // 담은 상품 기준으로 뽑는 필터 옵션(브랜드/카테고리) 갱신
      queryClient.invalidateQueries({
        queryKey: ["pathpick", "basket", "filter-info", companyId],
      });
      // 담은 상품 탭 배지 카운트(작업 전/등록 신청함/주문함) 갱신
      queryClient.invalidateQueries({
        queryKey: ["pathpick", "basket", "counts", companyId],
      });
      info(t("path_pick:basket.remove_success"));
    },
    onError: (err) => {
      error(err);
    },
  });
};

export default useDeletePathPickBasket;
